/**
 * WhoWeAreSection — the "WHO WE ARE: ARTISTS 4 LIFE IN THE SOUND" block.
 *
 * Single source of truth for this section. Mounted on:
 *   • /bands (BandsLandingPage)
 *   • Site footer (Footer.jsx, just above "THE WALL: DRIVEN BY SOUND")
 *
 * The footer wraps it in a light-themed surface so the dark footer doesn't
 * fight the section's mostly-light typography.
 */
import { Link } from "react-router-dom";

const POSTERS = [
  { year: "2019", url: "https://customer-assets.emergentagent.com/job_211279cf-e69f-4285-b28c-6209ba7e3f92/artifacts/5x2bauna_mhwp%202019%20origins%20website%20capture.jpg",     caption: "2019 · Origins" },
  { year: "2025", url: "https://customer-assets.emergentagent.com/job_211279cf-e69f-4285-b28c-6209ba7e3f92/artifacts/8cufy66y_mhwp-2025-ufo-eruption-poster-full-list-drop-full-poster-final-3333-png_orig.png", caption: "2025 · UFO Eruption" },
  { year: "2024", url: "https://customer-assets.emergentagent.com/job_211279cf-e69f-4285-b28c-6209ba7e3f92/artifacts/toq61oe1_mhwp-2024-lightning-haze-good-karma-poster-final-3333_orig.jpg", caption: "2024 · Angel Oak / Lightning Haze" },
  { year: "2023", url: "https://customer-assets.emergentagent.com/job_211279cf-e69f-4285-b28c-6209ba7e3f92/artifacts/nyacuk7y_mhwp-2023-poster-lancelot-final-color_orig.jpg",  caption: "2023 · Lancelot / The Wall debut" },
  { year: "2022", url: "https://customer-assets.emergentagent.com/job_211279cf-e69f-4285-b28c-6209ba7e3f92/artifacts/cu7xm0mz_mhwp-2022-poster-1111-w-thred-up-print-jpg_orig.jpg", caption: "2022 · 11:11 / Book of Kells stage" },
];

const WhoWeAreSection = ({ context = "page" }) => {
  // `context="footer"` opts into a slight visual treatment so the dark-themed
  // footer renders this section on a light-cream panel.
  const wrapperCls =
    context === "footer"
      ? "rounded-2xl bg-[#FAF9F6] px-6 sm:px-10 py-10 sm:py-12"
      : "";

  return (
    <section className={`mb-12 ${wrapperCls}`} data-testid="who-we-are-section">
      <h2 className="font-['Playfair_Display'] text-3xl sm:text-4xl lg:text-5xl font-bold text-[#1A1A1A] mb-4 leading-tight">
        <span className="text-[#1A1A1A]">WHO WE ARE: </span>
        <a
          href="https://www.a4s.us"
          target="_blank" rel="noopener noreferrer"
          className="underline decoration-2 underline-offset-4 hover:decoration-[#E63946]"
          data-testid="who-we-are-a4s-link"
        >
          <span style={{ color: "#3FA9F5" }}>ARTISTS </span>
          <span style={{ color: "#E63946" }}>4</span>
          <span style={{ color: "#3FA9F5" }}> LIFE IN THE SOUND</span>
        </a>
      </h2>
      <p className="text-lg sm:text-xl lg:text-2xl text-[#3a3a3a] max-w-3xl mb-5 leading-relaxed">
        Driven by Sound, THE WALL is an emergent innovative solution serving diverse
        community needs from the grass roots in WA state. We combine music, business
        and art to support regional, service-based organizations in our region, and
        awareness in communities wherever music roams... THE WALL originated in 2017,
        emerging from Stars 4 Peace, then combined with a bedrock NW cultural music
        festival to drive this mission with Sound.
      </p>
      <p className="text-sm sm:text-base text-[#2E5C55] mb-5">
        <a
          href="/our-origins"
          className="underline font-semibold hover:text-[#1f3f3a]"
          data-testid="who-we-are-origin-story-link"
        >
          Read the full origin story &rarr;
        </a>
      </p>
      <div className="flex gap-3 overflow-x-auto pb-2 -mx-4 px-4 sm:mx-0 sm:px-0" data-testid="who-we-are-poster-strip">
        {POSTERS.map(p => (
          <a
            key={p.year}
            href={p.url}
            target="_blank" rel="noopener noreferrer"
            className="flex-shrink-0 w-48 sm:w-56 group/p no-underline"
            data-testid={`mhwp-poster-${p.year}`}
          >
            <div className="aspect-square rounded-xl overflow-hidden bg-black ring-2 ring-[#FFB347]/30 group-hover/p:ring-[#FFB347]/80 transition-all shadow-md">
              <img
                src={p.url}
                alt={`MHWP ${p.year} poster`}
                className="w-full h-full object-cover group-hover/p:scale-[1.03] transition-transform duration-500"
                loading="lazy"
              />
            </div>
            <p className="text-xs text-[#5C5C5C] mt-2 leading-snug">{p.caption}</p>
          </a>
        ))}
      </div>
      <p className="text-xs text-[#5C5C5C] italic mt-3">
        Your tour joins a lineage of artists who chose to defend the scene by being part of it.
      </p>

      {/* A4S.US closing link */}
      <p className="mt-6 text-sm tracking-[0.2em] font-bold">
        <Link
          to="/"
          as="a"
          onClick={(e) => { e.preventDefault(); window.open("https://www.a4s.us", "_blank", "noopener,noreferrer"); }}
          className="text-[#2E5C55] hover:text-[#1f3f3a] no-underline"
          data-testid="who-we-are-a4s-end-link"
        >
          A4S.US &rarr;
        </Link>
      </p>
    </section>
  );
};

export default WhoWeAreSection;
